import { FC, useRef, useState } from "react";

import { useAtom, useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import {
  Text,
  Group,
  Modal,
  Stack,
  Title,
  Button,
  Divider,
  createStyles,
} from "@mantine/core";

import { Form } from "@components/Form/Form";
import { FormProvider } from "@components/FormProvider/FormProvider";

import merge from "lodash/merge";

import { Step1 as VolunteeringFormStep1 } from "@routes/VolunteeringFormRoute/components/Step1";
import { Step2 as VolunteeringFormStep2 } from "@routes/VolunteeringFormRoute/components/Step2";

import { Step1 as EventRegistrationFormStep1 } from "@routes/EventRegistrationFormRoute/components/Step1";

import { Step1 as DemoRegistrationFormStep1 } from "@routes/DemoRegistrationFormRoute/components/Step1";
import { Step2 as DemoRegistrationFormStep2 } from "@routes/DemoRegistrationFormRoute/components/Step2";

import { Draft } from "@utils/Draft.util";
import { selectedUserIdAtom } from "../atoms/selectedUserId.atom";
import { selectedFormNameAtom } from "../atoms/selectedFormName.atom";

import { IStepRef } from "@models/Step.model";
import { IDemoRegistrationForm } from "@models/DemoRegistrationForm.model";
import { IEventRegistrationForm } from "@models/EventRegistrationForm.model";
import { IIVolunteeringFormData } from "@routes/VolunteeringFormRoute/VolunteeringFormRoute.types";

const useStyles = createStyles((t) => ({
  form: {
    paddingTop: "unset !important",
  },
}));

export const SubmissionModal: FC = () => {
  const step1Ref = useRef<IStepRef<any>>(null);
  const step2Ref = useRef<IStepRef<any>>(null);

  const { classes } = useStyles();

  const { t } = useTranslation();

  const selectedFormName = useAtomValue(selectedFormNameAtom);
  const [selectedUserId, setSelectedUserId] = useAtom(selectedUserIdAtom);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const isOpened = !!selectedUserId;

  if (!selectedUserId) return <></>;
  return (
    <>
      <Modal
        centered
        size={700}
        opened={isOpened}
        onClose={() => {
          if (step1Ref.current?.isDirty() || step2Ref.current?.isDirty())
            setIsConfirmModalVisible(true);
          else setSelectedUserId(null);
        }}
      >
        <FormProvider
          formName={selectedFormName}
          uid={selectedUserId}
          ignoreSubmission={true}
        >
          {({ draft, onForceSaveChanges, isSaving, user, isConfirmed }) => (
            <>
              {selectedFormName === "volunteering" && (
                <Form
                  totalSteps={2}
                  currentStep={1}
                  isSaving={false}
                  showSteps={false}
                  showHeader={false}
                  showDescription={false}
                  title="Volunteering"
                  description=""
                  className={classes.form}
                >
                  <Stack>
                    <VolunteeringFormStep1
                      ref={step1Ref}
                      readonly
                      user={user}
                      draft={draft as Draft<IIVolunteeringFormData>}
                    />
                    <Divider />
                    <VolunteeringFormStep2
                      ref={step2Ref}
                      readonly
                      draft={draft as Draft<IIVolunteeringFormData>}
                    />
                  </Stack>
                </Form>
              )}
              {selectedFormName === "event-registration" && (
                <Form
                  totalSteps={2}
                  currentStep={1}
                  isSaving={false}
                  showSteps={false}
                  showHeader={false}
                  showDescription={false}
                  title="Event Registration"
                  description=""
                  className={classes.form}
                >
                  <Stack>
                    <EventRegistrationFormStep1
                      readonly
                      user={user}
                      ref={step1Ref}
                      uid={selectedUserId}
                      isConfirmed={isConfirmed}
                      draft={draft as Draft<IEventRegistrationForm>}
                    />
                  </Stack>
                </Form>
              )}

              {selectedFormName === "demo-registration" && (
                <Form
                  totalSteps={2}
                  currentStep={1}
                  isSaving={false}
                  showSteps={false}
                  showHeader={false}
                  showDescription={false}
                  title="Demonstration Registration"
                  description=""
                  className={classes.form}
                >
                  <Stack>
                    <DemoRegistrationFormStep1
                      ref={step1Ref}
                      readonly
                      draft={draft as Draft<IDemoRegistrationForm>}
                    />
                    <Divider />
                    <DemoRegistrationFormStep2
                      ref={step2Ref}
                      readonly
                      draft={draft as Draft<IDemoRegistrationForm>}
                    />
                  </Stack>
                </Form>
              )}
              <Modal
                opened={isConfirmModalVisible}
                onClose={() => {
                  setIsConfirmModalVisible(false);
                }}
              >
                <Title mb="sm" size="sm">
                  {t("Confirm Changes")}
                </Title>
                <Text color="dimmed" size="sm">
                  {t(
                    'Please review the changes before submitting. If you are satisfied with the changes, click on "Save" to submit. If not, click on "Discard" to discard the changes and return to the previous state.',
                  )}
                </Text>
                <Group mt="lg" position="right" spacing="xs">
                  <Button
                    loading={isSaving}
                    onClick={() => {
                      const changes = merge(
                        step1Ref.current?.getValues() || {},
                        step2Ref.current?.getValues() || {},
                      );
                      onForceSaveChanges(changes || {}).finally(() => {
                        setIsConfirmModalVisible(false);
                        setSelectedUserId(null);
                        step1Ref.current?.resetDirty();
                        step2Ref.current?.resetDirty();
                      });
                    }}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsConfirmModalVisible(false);

                      setSelectedUserId(null);
                    }}
                  >
                    {t("Discard")}
                  </Button>
                </Group>
              </Modal>
              <Group mt="lg" position="center" spacing="xs">
                <Button
                  loading={isSaving}
                  onClick={() => {
                    const changes = merge(
                      step1Ref.current?.getValues() || {},
                      step2Ref.current?.getValues() || {},
                    );
                    onForceSaveChanges(changes || {}).finally(() => {
                      step1Ref.current?.resetDirty();
                      step2Ref.current?.resetDirty();
                    });
                  }}
                >
                  {t("Save Changes")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsConfirmModalVisible(false);

                    setSelectedUserId(null);
                  }}
                >
                  {t("Discard Changes")}
                </Button>
              </Group>
            </>
          )}
        </FormProvider>
      </Modal>
    </>
  );
};
